<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { TRASH_RETENTION_DAYS, type ItemDTO } from "@barkup/shared";
import { fetchItemThumbUrl } from "../api.js";
import { fileIconFor } from "../fileIcon.js";
import { formatBytes, formatShortDate, itemTitle, senderLabel } from "../format.js";
import { useLongPress } from "../composables/useLongPress.js";
import { highlightSegments } from "../search.js";

/**
 * Compact file-manager row (the Drive/OneDrive pattern users already know):
 * tap opens, long-press enters selection mode, the single ⋮ opens a bottom
 * sheet with everything else. Replaces the old always-visible checkbox +
 * per-row action-button strip, which made every row ~140px of chrome.
 */
const props = defineProps<{
  item: ItemDTO;
  selected: boolean;
  selectionMode: boolean;
  /** trash rows lead their metadata with the days left before permanent purge */
  trash?: boolean;
  /**
   * The active search query, for emphasising the matched run inside the title.
   * Emphasis is weight-and-ink only — no highlighter fill. A tinted background
   * would either borrow the primary (reserved for things you can act on) or the
   * duplicate tag's apricot, and this is neither.
   *
   * Absent emphasis is a real outcome, not a miss: an item can match on its URL
   * while the row shows its page title, and pointing at text that didn't match
   * would be worse than pointing at nothing.
   */
  highlight?: string;
}>();

const emit = defineEmits<{
  open: [id: string];
  "toggle-select": [id: string];
  "enter-select": [id: string];
  menu: [item: ItemDTO];
}>();

const kindMeta = computed(
  () =>
    ({
      FILE: { icon: fileIconFor(props.item), tile: "bg-primary-container text-primary" },
      IMAGE: { icon: "image", tile: "bg-secondary-container text-secondary" },
      VIDEO: { icon: "movie", tile: "bg-surface-container-high text-on-surface-variant" },
      LINK: { icon: "link", tile: "bg-tertiary-container text-tertiary" },
    })[props.item.kind]
);

const title = computed(() => itemTitle(props.item));
const titleSegments = computed(() => highlightSegments(title.value, props.highlight ?? ""));
// The title recedes only when there is something in it to recede *behind*. An
// item can match on its URL while the row shows its page title — dimming that
// row's title with nothing emphasised would just make it look disabled.
const titleHasHit = computed(() => titleSegments.value.some((seg) => seg.hit));

// One metadata line, Drive-style: who • when • size. READY is the normal
// state and is no longer spelled out per row — only the exceptions surface
// (as inline tags next to the title, see template). Trash rows lead with the
// purge countdown instead of the send date — in the trash, "how long until
// this is gone forever" is the date that matters.
const trashDaysLeft = computed(() => {
  if (!props.trash || !props.item.deletedAt) return null;
  const elapsed = Math.floor((Date.now() - new Date(props.item.deletedAt).getTime()) / 86_400_000);
  return Math.max(0, TRASH_RETENTION_DAYS - elapsed);
});

const metaLine = computed(() => {
  const parts =
    trashDaysLeft.value !== null
      ? [`เหลืออีก ${trashDaysLeft.value} วัน`, senderLabel(props.item)]
      : [senderLabel(props.item), formatShortDate(props.item.createdAt)];
  if (Number(props.item.sizeBytes)) parts.push(formatBytes(props.item.sizeBytes));
  return parts.join(" • ");
});

const isPartialScan = computed(() => props.item.status === "PARTIAL_SCAN");
const isBlockedish = computed(() => props.item.status !== "READY" && props.item.status !== "PARTIAL_SCAN");

// Real preview in the tile for images with a server thumbnail (~tens of KB,
// cached in api.ts's object-URL cache). Everything else keeps the kind icon.
const thumbUrl = ref<string | null>(null);
watch(
  () => props.item.id,
  (itemId) => {
    thumbUrl.value = null;
    if (props.item.kind !== "IMAGE" || !props.item.hasThumb) return;
    fetchItemThumbUrl(itemId)
      .then((url) => {
        if (props.item.id === itemId) thumbUrl.value = url;
      })
      .catch(() => void 0); // icon fallback — a failed thumb is cosmetic here
  },
  { immediate: true }
);

const { onPointerdown, onPointermove, onPointerup, consumeLongPress } = useLongPress(() =>
  emit("enter-select", props.item.id)
);

function onRowClick(): void {
  if (consumeLongPress()) return; // the release of a long-press is not a tap
  if (props.selectionMode) emit("toggle-select", props.item.id);
  else emit("open", props.item.id);
}
</script>

<template>
  <div
    class="item-card-root flex select-none items-center gap-3 rounded-xl px-2 py-2 transition-colors duration-150"
    :class="selected ? 'bg-primary-container/40' : 'active:bg-surface-container'"
    role="button"
    tabindex="0"
    :aria-label="title"
    :aria-pressed="selectionMode ? selected : undefined"
    @pointerdown="onPointerdown"
    @pointermove="onPointermove"
    @pointerup="onPointerup"
    @pointercancel="onPointerup"
    @click="onRowClick"
    @keydown.enter="onRowClick"
    @contextmenu.prevent
  >
    <!-- Leading tile: thumbnail or kind icon; flips to a check when selected (Drive behavior). -->
    <span
      class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg transition-colors duration-150"
      :class="selected ? 'bg-primary text-on-primary' : kindMeta.tile"
    >
      <span v-if="selected" class="material-symbols-outlined text-[22px]">check</span>
      <img v-else-if="thumbUrl" :src="thumbUrl" alt="" class="h-full w-full object-cover" draggable="false" />
      <span v-else class="material-symbols-outlined text-[22px]">{{ kindMeta.icon }}</span>
    </span>

    <span class="min-w-0 flex-1">
      <span class="flex items-center gap-1.5">
        <!-- Whitespace between the segment tags is deliberately eliminated (the
             `><` line breaks): inside a `truncate` span, a stray text node would
             render as a real space in the middle of a filename. -->
        <span
          class="truncate font-body-md"
          :class="titleHasHit ? 'font-normal text-on-surface-variant' : 'font-medium text-on-surface'"
          ><template v-for="(seg, i) in titleSegments" :key="i"
            ><span v-if="seg.hit" class="font-semibold text-on-surface">{{ seg.text }}</span
            ><template v-else>{{ seg.text }}</template></template
          ></span
        >
        <span
          v-if="item.isDuplicate"
          class="shrink-0 rounded-full bg-secondary-container px-2 py-0.5 text-[10px] font-bold text-on-secondary-container"
          >ซ้ำ</span
        >
        <!-- Honest-about-limits (PRODUCT.md): the partial-scan label must stay visible, but as
             information, not alarm — outline style, no error color. -->
        <span
          v-if="isPartialScan"
          class="shrink-0 rounded-full border border-outline-variant px-2 py-0.5 text-[10px] font-bold text-on-surface-variant"
          >ยังไม่สแกนครบ</span
        >
        <span
          v-else-if="isBlockedish"
          class="shrink-0 rounded-full bg-error-container px-2 py-0.5 text-[10px] font-bold text-on-error-container"
          >BLOCKED</span
        >
      </span>
      <span class="block truncate font-metadata text-on-surface-variant">{{ metaLine }}</span>
    </span>

    <!-- Selection mode swaps the ⋮ for a check indicator so the row has exactly one trailing affordance. -->
    <span
      v-if="selectionMode"
      class="flex h-11 w-11 shrink-0 items-center justify-center"
      aria-hidden="true"
    >
      <span
        class="flex h-6 w-6 items-center justify-center rounded-full border transition-colors duration-150"
        :class="selected ? 'border-primary bg-primary text-on-primary' : 'border-outline'"
      >
        <span v-if="selected" class="material-symbols-outlined text-[16px]">check</span>
      </span>
    </span>
    <button
      v-else
      type="button"
      aria-label="ตัวเลือกเพิ่มเติม"
      class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-on-surface-variant transition-colors duration-150 active:bg-surface-container-high"
      @pointerdown.stop
      @click.stop="emit('menu', item)"
    >
      <span class="material-symbols-outlined text-[20px]">more_vert</span>
    </button>
  </div>
</template>

<style scoped>
/* Skips layout/paint for rows scrolled out of view in a long archive; the
   estimate keeps scrollbar/scroll-position stable, and scrolling a row back
   into view always renders its real, current size. */
.item-card-root {
  content-visibility: auto;
  contain-intrinsic-size: auto 56px;
}
</style>
